import React, { forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { Form, Select, Radio, Icon } from 'antd';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';

import Modal from 'common/components/modal';
import { depositTransferTime, rentTransferTime, apCategoryEnum } from '~constants/landlord';

const useStyles = makeStyles(theme => ({
  modalWrapper: {
    '& .ant-modal-body': {
      padding: '0 32px',

      '& .ant-descriptions-item-label': {
        padding: '9.25px 17px',
        color: theme.colors.bodyColor,
        backgroundColor: theme.colors.gray3,
      },

      '& .ant-descriptions-item-content': {
        color: theme.colors.bodyColor,
        padding: '9.25px 17px',
      },

      '& .ant-form-item': {
        paddingBottom: 0,
      },

      '& .ant-form-item-label': {
        '& > label': {
          color: 'rgba(0, 0, 0, 0.65) !important',
        },
        fontSize: 14,
        lineHeight: '22px',
        paddingBottom: 8,
      },
    },
  },
  subtitle: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 16,
    lineHeight: '24px',
    fontWeight: 'bold',
    color: 'rgba(0, 0, 0, 0.85)',
    marginBottom: 16,

    '&::before': {
      content: '""',
      height: 16,
      width: 3,
      backgroundColor: '#38b2a6',
      marginRight: 8,
    },
  },
  hintBox: {
    ...theme.flex.row,
    marginTop: 8,
  },
  noPaymentFont: {
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 400,
    color: theme.colors.orange6,
  },
}));

const { Option } = Select;

const APCategoryEditModal = forwardRef(
  (
    {
      landlord,
      form,
      showAPCategoryEditModal,
      handleCancelModal,
      updateLandlord,
      updateCallback,
      updateLandlordLoading,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const { paymentPlans, allowNoPayment } = landlord;
    const { getFieldValue } = form;
    const hasNoPaymentPlan = paymentPlans?.some(paymentPlan => !paymentPlan?.paymentOnline);
    console.log('hasNoPaymentPlan', hasNoPaymentPlan);
    useImperativeHandle(ref, () => ({
      handleOpenModal,
    }));

    const handleSubmit = () => {
      form.validateFields((err, values) => {
        if (!err) {
          const params = {
            id: landlord.id,
            depositTransferTime: values.deposit,
            rentTransferTime: values.rent,
            apCategory: values.apCategory,
            allowNoPayment: values.allowNoPayment,
          };

          updateLandlord(params, () => {
            handleCancelModal();
            updateCallback();
          });
        }
      });
    };
    const showNoPaymentHint =
      allowNoPayment &&
      !getFieldValue('allowNoPayment') &&
      getFieldValue('allowNoPayment') !== undefined &&
      hasNoPaymentPlan;

    console.log(222);
    return (
      <Modal
        visible={showAPCategoryEditModal}
        width={454}
        title={t('cms.landlord.detail.ap_category.edit_title')}
        wrapClassName={classes.modalWrapper}
        onCancel={handleCancelModal}
        okText={t('cms.landlord.modal.confirm.button')}
        cancelText={t('cms.landlord.modal.cancel.button')}
        onOk={handleSubmit}
        okButtonProps={{ loading: updateLandlordLoading }}
      >
        <Form layout="vertical">
          <div className={classes.subtitle}>
            {t('cms.landlord.detail.ap_category.cashflow_management.title')}
          </div>
          <Form.Item label={`${t('cms.landlord.detail.ap_category.label')}:`}>
            {form.getFieldDecorator('apCategory', {
              rules: [{ required: true }],
              initialValue: landlord?.apCategory ?? apCategoryEnum[0],
            })(
              <Select>
                {apCategoryEnum.map(item => (
                  <Option value={item}>
                    {t(`cms.landlord.detail.ap_category.option.${item.toLowerCase()}`)}
                  </Option>
                ))}
              </Select>,
            )}
          </Form.Item>
          <Form.Item label={`${t('cms.landlord.detail.ap_category.deposit')}:`}>
            {form.getFieldDecorator('deposit', {
              rules: [{ required: true }],
              initialValue: landlord.depositTransferTime ?? depositTransferTime[0],
            })(
              <Select>
                {depositTransferTime.map(item => (
                  <Option value={item}>
                    {t(`cms.landlord.detail.ap_category.deposit.${item.toLowerCase()}`)}
                  </Option>
                ))}
              </Select>,
            )}
          </Form.Item>
          <Form.Item
            label={`${t('cms.landlord.detail.ap_category.rent')}:`}
            style={{ marginBottom: 32 }}
          >
            {form.getFieldDecorator('rent', {
              rules: [{ required: true }],
              initialValue: landlord.rentTransferTime ?? rentTransferTime[0],
            })(
              <Select>
                {rentTransferTime.map(item => (
                  <Option value={item}>
                    {t(`cms.landlord.detail.ap_category.rent.${item.toLowerCase()}`)}
                  </Option>
                ))}
              </Select>,
            )}
          </Form.Item>
          <div className={classes.subtitle}>
            {t('cms.landlord.detail.ap_category.no_payment_access.title')}
          </div>
          <Form.Item
            label={`${t('cms.landlord.detail.ap_category.no_payment_access.title')}:`}
            style={{ marginBottom: 32 }}
          >
            {form.getFieldDecorator('allowNoPayment', {
              initialValue: landlord.allowNoPayment ?? false,
            })(
              <Radio.Group>
                <Radio value={false}>
                  {t('cms.landlord.detail.ap_category.no_payment_access.denied')}
                </Radio>
                <Radio value={true}>
                  {t('cms.landlord.detail.ap_category.no_payment_access.allowed')}
                </Radio>
              </Radio.Group>,
            )}
            <If condition={showNoPaymentHint}>
              <div className={classes.hintBox} style={{}}>
                <Icon
                  type="exclamation-circle"
                  theme="filled"
                  style={{ color: '#f9a600', marginRight: 8, fontSize: 12, marginTop: 4 }}
                />
                <span className={classes.noPaymentFont}>
                  {t('cms.landlord.detail.no_payment_access.not_allowed.hint')}
                </span>
              </div>
            </If>
          </Form.Item>
        </Form>
      </Modal>
    );
  },
);

APCategoryEditModal.displayName = 'APCategoryEditModal';

APCategoryEditModal.propTypes = {
  landlord: PropTypes.object.isRequired,
};

export default Form.create()(APCategoryEditModal);
